import { useCallback } from 'react';
import { useCreateProject } from '@atlassian/jira-project-create-common/src/services/create-project/index.tsx';
import type {
	CreateProjectBody,
	CreateProjectResponse,
} from '@atlassian/jira-project-create-common/src/services/create-project/types.tsx';
import {
	PACKAGE_NAME,
	PROJECT_CREATE_APP_NAME,
	TEAM_NAME,
} from '@atlassian/jira-project-create-common/src/constants.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { getValidProjectName } from './utils.tsx';

type UseSubmitProjectCreateReturn = [
	(payload: CreateProjectBody) => Promise<CreateProjectResponse | undefined>,
	isLoading: boolean,
	hasError: boolean,
];

export const useSubmitProjectCreate = (
	onSuccess: (project: CreateProjectResponse) => void,
	onFail: (error: Error) => void,
): UseSubmitProjectCreateReturn => {
	const [createProject, isLoading, hasError] = useCreateProject();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const submitProjectCreate = useCallback(
		async (body: CreateProjectBody) => {
			try {
				const validName = await getValidProjectName(body.payload.name);
				const result = await createProject({
					...body,
					payload: { ...body.payload, name: validName },
				});

				fireTrackAnalytics(createAnalyticsEvent({}), 'submitProjectCreate success');
				if (result !== undefined) {
					onSuccess(result);
				}
				return result;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: `${PROJECT_CREATE_APP_NAME}.use-create-project-with-shared-config.error`,
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					error,
				});

				onFail(error);
			}
		},
		[createAnalyticsEvent, createProject, onFail, onSuccess],
	);

	return [submitProjectCreate, isLoading, hasError];
};
